import React, { useEffect, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { printService } from 'api/services/printService';
import type { Printer, PrintJob } from 'api/types';
import { JobStatusChip, fmtTime, apiMessage } from 'components/modules/print-module/printUi';

/**
 * "Print test page": queues a test page on one printer and follows the job
 * until the agent reports it sent, printed or failed (or a minute passes).
 * An info dialog: the backdrop closes it.
 *
 * The test page is sent by the caller's click (queueTestPage), never from an
 * effect here: React StrictMode runs effects twice in development, which would
 * print two pages. This dialog only follows the job it is handed.
 */
interface Props {
  printer: Printer | null;
  /** The send already made by the click, from queueTestPage(printer). */
  queued: Promise<PrintJob> | null;
  onClose: () => void;
}

/** Send one test page; call from the click handler and hand the promise to the dialog. */
export const queueTestPage = (printer: Printer): Promise<PrintJob> => {
  const p = printService.testPrinter(printer.id);
  p.catch(() => { /* reported by the dialog */ });
  return p;
};

const DONE = new Set(['SENT', 'PRINTED', 'FAILED', 'CANCELLED', 'REROUTED']);
const FOLLOW_MS = 60_000;

const PrintTestPageModal: React.FC<Props> = ({ printer, queued, onClose }) => {
  const [job, setJob] = useState<PrintJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (!printer || !queued) return;
    let alive = true;
    const started = Date.now();
    setJob(null); setError(null); setWaiting(true);
    const poll = async (id: string) => {
      try {
        const j = await printService.getJob(id);
        if (!alive) return;
        setJob(j);
        if (DONE.has(j.jobStatus)) { setWaiting(false); return; }
      } catch { if (!alive) return; /* keep the last answer */ }
      // The cut-off applies to errors too: a failing status call must not poll forever.
      if (Date.now() - started > FOLLOW_MS) { setWaiting(false); return; }
      timer.current = window.setTimeout(() => void poll(id), 1500);
    };
    queued
      .then((j) => { if (!alive) return; setJob(j); void poll(j.id); })
      .catch((err) => { if (!alive) return; setWaiting(false); setError(apiMessage(err, 'The test page could not be queued.')); });
    return () => { alive = false; if (timer.current) window.clearTimeout(timer.current); };
  }, [printer, queued]);

  if (!printer) return null;
  const done = job && DONE.has(job.jobStatus);

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-sm" role="dialog" aria-modal="true" style={{ minHeight: 'auto', maxWidth: 460 }} data-test-page>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Test page — {printer.printerName}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close"><IconSystem.x size={18} /></button>
        </div>
        <div className="modal-content">
          {error && <div className="form-error-message" data-test-error>{error}</div>}
          {!error && !job && <p style={{ margin: 0 }}>Queuing the test page…</p>}
          {job && (
            <>
              <p style={{ marginTop: 0 }}>
                Status: <span data-test-status={job.jobStatus}><JobStatusChip job={job} /></span>
                {waiting && !done && <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontSize: 12 }}>waiting for {job.agentName ?? 'the agent'}…</span>}
              </p>
              <dl className="prn-kv">
                <dt>Printer</dt><dd>{job.printerName ?? printer.printerName}</dd>
                <dt>Agent</dt><dd>{job.agentName ?? '—'}{job.agentOnline ? '' : ' (offline: the page waits until it is back)'}</dd>
                <dt>Queued</dt><dd>{fmtTime(job.createdAt)}</dd>
                {job.sentAt && <><dt>Sent</dt><dd>{fmtTime(job.sentAt)}</dd></>}
                {job.lastError && <><dt>Error</dt><dd style={{ color: 'var(--danger, #dc2626)' }}>{job.lastError}</dd></>}
              </dl>
              {!done && !waiting && (
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                  Not printed yet. It stays in the Print Queue and prints when the agent and printer are reachable.
                </p>
              )}
            </>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

export default PrintTestPageModal;
